import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';

import { ToastManager } from '../../ToastManager';

import { useEventCallback } from '../../hooks/useEventCallback';
import { useI18n } from '../../hooks/useI18nFormatters';

import { containers } from '../../theme/elements/containers';
import { scrollbarDark } from '../../theme/elements/scrollbars';
import { su } from '../../theme/functions/su';
import { theme } from '../../theme/theme';

import { TextSmall } from '../../components/common/text/TextSmall';

import { useScripts } from './useScripts';

export function ScriptOutput(props) {
  const { i18n } = useI18n();

  const scrollRef = useRef();

  const { appInstance } = useScripts();
  const [logs, setLogs] = useState([]);

  const handleLog = useEventCallback(({ text, script }) => {
    if (script !== props.scriptId) {
      return;
    }

    console.log(text);
    setLogs((prevLogs) => [...prevLogs, text]);
  });

  useEffect(() => {
    setLogs([]);
  }, [props.scriptId]);

  useEffect(() => {
    if (appInstance == null) {
      return;
    }

    appInstance.on('log', handleLog);

    return function () {
      appInstance.off('log', handleLog);
    };
  }, [appInstance, handleLog]);

  useEffect(() => {
    if (appInstance == null) {
      return;
    }

    const leasePromise = appInstance.acquireConnection().catch((error) => {
      ToastManager.handleError(error);
      return null;
    });

    return function () {
      leasePromise
        .then(async (lease) => {
          await lease?.release();
        })
        .catch(ToastManager.handleError);
    };
  }, [appInstance]);

  useEffect(() => {
    const scrollElement = scrollRef.current;

    if (scrollElement != null) {
      scrollElement.scrollTop = scrollElement.scrollHeight - scrollElement.clientHeight;
    }
  }, [logs]);

  return (
    <ScriptOutput.Root>
      <ScriptOutput.Content ref={scrollRef}>
        {logs.length === 0 && (
          <ScriptOutput.Empty>
            {i18n.messageFormatter('script.consolePlaceholder')}
          </ScriptOutput.Empty>
        )}

        {logs.map((log, index) => {
          return <pre key={index}>{log}</pre>;
        })}
      </ScriptOutput.Content>
    </ScriptOutput.Root>
  );
}

ScriptOutput.Root = styled(TextSmall)`
  ${containers.card};
  display: flex;
  flex-direction: column;

  flex: 1 1 0;
  padding: ${su(2)} 0;

  overflow: hidden;

  &,
  pre {
    font-family:
      Roboto Mono,
      monospace;
    font-feature-settings:
      'liga' 0,
      'calt' 0;
    white-space: pre-wrap;
  }
`;

ScriptOutput.Content = styled.div`
  ${scrollbarDark};

  flex: 1 1 0;
  padding: 0 ${su(2)};

  overflow-y: auto;
`;

ScriptOutput.Empty = styled.div`
  color: ${theme.color.text_light};
`;
